import { defineWidgetCore } from "../../widgetDefinition";
import { resolveTemplate } from "../../Utils/resolveTemplate";
import type { StatComponentSpec, StatTileNonSeriesResponse, WidgetResponseSpec } from "../../Processing/types";
import { renderStat } from "../../PDF/renderers/statPdfRenderer";
import { toLegacyStatTile } from "@utils/statTilesV2.mjs";

const EMPTY_RESPONSE: WidgetResponseSpec = { widget_id: "", series: [] };

export const statCore = defineWidgetCore<StatComponentSpec>({
  process(spec, response) {
    const s = spec;
    const res = response ?? EMPTY_RESPONSE;
    const legacy = (res.non_series_response ?? null) as StatTileNonSeriesResponse | null;
    // An ok stat tiles API answer replaces the number fields. The benchmarks and the
    // name stay legacy: the dashboard formats them its own way. The screen and the PDF
    // snapshot both read this object, so the export shows the numbers on screen.
    const adapted = legacy?.v2?.status === "ok" ? toLegacyStatTile(legacy.v2) : null;
    const metricStat: StatTileNonSeriesResponse | null =
      legacy && adapted ? { ...legacy, ...adapted, team: legacy.team, pos: legacy.pos, name: legacy.name } : legacy;

    return {
      type: "stat_tile",
      spec: s,
      stat: {
        metricStat,
        label: s.display?.title ?? legacy?.name ?? "",
      },
    };
  },
  pdf: {
    buildSnapshot({ processed, slicerValues }) {
      if (processed.type !== "stat_tile") return null;
      const ms = processed.stat.metricStat;
      const rawLabel = processed.stat.label || processed.spec.display.title || "";
      const label = resolveTemplate(rawLabel, slicerValues).resolved || "—";
      const metricAgg = processed.spec.stat_tile_builder_misc?.metricAgg ?? "avg";

      // The backend reports the decimal count the admin panel configured for the metric.
      const numDecimals = Number.isInteger(ms?.num_decimals) ? (ms!.num_decimals as number) : null;
      const fmtVal = (v: number | string | null | undefined): string | null => {
        if (v == null) return null;
        const n = Number(v);
        if (isNaN(n)) return String(v);
        return numDecimals != null ? n.toFixed(numDecimals) : String(Math.round((n + Number.EPSILON) * 100) / 100);
      };

      const showTeamPosData = processed.spec.stat_tile_builder_misc?.showTeamPosData !== false;

      if (!ms) {
        return { type: "stat_tile", label, metricAgg, latestDate: null, isAsym: false, latestValue: null, diff: null, lowGood: false, left: null, right: null, team: null, pos: null, showTeamPosData, numDecimals: null };
      }

      return {
        type: "stat_tile",
        label: label || ms.name || "—",
        metricAgg,
        latestDate: ms.latest_date
          ? `Latest date: ${new Date(ms.latest_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
          : null,
        isAsym: ms.is_asym,
        latestValue: fmtVal(ms.latest_value),
        diff: ms.diff,
        lowGood: ms.low_good,
        left: ms.left ? { value: fmtVal(ms.left.latest_value) ?? "—", diff: ms.left.diff } : null,
        right: ms.right ? { value: fmtVal(ms.right.latest_value) ?? "—", diff: ms.right.diff } : null,
        team: showTeamPosData ? ms.team : null,
        pos: showTeamPosData ? ms.pos : null,
        showTeamPosData,
        numDecimals,
      };
    },
    render({ snapshot, rect, scale }) {
      if (snapshot.type !== "stat_tile") return null;
      return renderStat(snapshot, rect, scale);
    },
  },
});
